/**
 * Students API Slice
 *
 * RTK Query API slice for Student Management feature endpoints.
 * Handles student CRUD operations and Excel import/export.
 */

import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQueryWithReauth } from './baseQuery';
import type {
  Student,
  StudentFormData,
  StudentUploadResponse,
  StudentListResponse,
} from '../types/students';

export const studentsApi = createApi({
  reducerPath: 'studentsApi',
  baseQuery: baseQueryWithReauth,
  tagTypes: ['Student'],
  endpoints: builder => ({
    // ========================================================================
    // Student CRUD Endpoints
    // ========================================================================

    /**
     * Get students list for a studio
     */
    getStudents: builder.query<Student[], string>({
      query: studioId => `/studios/${studioId}/students/`,
      transformResponse: (response: Student[] | StudentListResponse) => {
        // Handle both array response and paginated response
        if (Array.isArray(response)) {
          return response;
        }
        return response.results;
      },
      providesTags: result =>
        result
          ? [
              ...result.map(({ id }) => ({ type: 'Student' as const, id })),
              { type: 'Student', id: 'LIST' },
            ]
          : [{ type: 'Student', id: 'LIST' }],
    }),

    /**
     * Create a new student
     */
    createStudent: builder.mutation<
      Student,
      { studioId: string; data: StudentFormData }
    >({
      query: ({ studioId, data }) => ({
        url: `/studios/${studioId}/students/`,
        method: 'POST',
        body: data,
      }),
      invalidatesTags: [{ type: 'Student', id: 'LIST' }],
    }),

    /**
     * Update an existing student
     */
    updateStudent: builder.mutation<
      Student,
      { studioId: string; studentId: string; data: Partial<StudentFormData> }
    >({
      query: ({ studioId, studentId, data }) => ({
        url: `/studios/${studioId}/students/${studentId}/`,
        method: 'PATCH',
        body: data,
      }),
      invalidatesTags: (result, error, { studentId }) => [
        { type: 'Student', id: studentId },
        { type: 'Student', id: 'LIST' },
      ],
    }),

    /**
     * Delete a student (soft-delete)
     */
    deleteStudent: builder.mutation<
      void,
      { studioId: string; studentId: string }
    >({
      query: ({ studioId, studentId }) => ({
        url: `/studios/${studioId}/students/${studentId}/`,
        method: 'DELETE',
      }),
      invalidatesTags: [{ type: 'Student', id: 'LIST' }],
    }),

    // ========================================================================
    // Excel Upload/Download Endpoints
    // ========================================================================

    /**
     * Upload Excel file to replace all students
     */
    uploadStudents: builder.mutation<
      StudentUploadResponse,
      { studioId: string; file: File }
    >({
      query: ({ studioId, file }) => {
        const formData = new FormData();
        formData.append('file', file);
        return {
          url: `/studios/${studioId}/students/upload/`,
          method: 'POST',
          body: formData,
        };
      },
      invalidatesTags: [{ type: 'Student', id: 'LIST' }],
    }),

    /**
     * Download blank Excel template
     * Note: This returns a blob for file download
     */
    downloadTemplate: builder.query<Blob, void>({
      query: () => ({
        url: '/students/template/',
        responseHandler: async (response: Response) => response.blob(),
      }),
    }),
  }),
});

// Export hooks for usage in components
export const {
  useGetStudentsQuery,
  useCreateStudentMutation,
  useUpdateStudentMutation,
  useDeleteStudentMutation,
  useUploadStudentsMutation,
  useLazyDownloadTemplateQuery,
} = studentsApi;
